// Hash routes. No router library: a hash keeps deep links (and the Back button) working without
// server-side rewrites. Pure: parse and format only.
import { type Department, isDepartment } from "./departments.ts";
import type { ElementSubtype } from "./types.ts";

/** Inside Makers: a brief (`new` for a blank one) or one assignment. */
export type MakersPage = { t: "brief"; id: string } | { t: "assignment"; id: string };

/** A folder inside a project: the left tree and the middle pane both point at one of these. */
export type Loc =
  | { kind: "root" }
  | { kind: "episodes" }
  /**
   * an episode: the production board, one department at a time (`dept` absent: the board picks one). `file` is the
   * file open on its own page in Writing: `script`, or a sheet's id
   */
  | { kind: "episode"; id: string; dept?: Department; file?: string }
  | { kind: "scene"; episodeId: string; id: string }
  /** the cut of an episode: its shots back to back, with notes and new takes (F5) */
  | { kind: "cut"; episodeId: string }
  /** the storyboard of an episode: a still per shot */
  | { kind: "storyboard"; episodeId: string }
  /** Create's Tools tab: one operation on a take of a shot (`shotId` absent: the first shot) */
  | { kind: "tools"; episodeId: string; shotId?: string }
  /**
   * Create's Edit tab (F12): the board cut, or a named edit (`editId`), with its notes in timecode order. The address
   * is /edit; the old /review opens the same screen. `open`: a FilmCraft edit full-screen in the editor (/edit/:id/open)
   */
  | { kind: "review"; episodeId: string; editId?: string; open?: boolean }
  /** Visualize > 3D: per shot the frame rendered in Blender next to the still */
  | { kind: "blocking"; episodeId: string }
  /** the script with its breakdown tags: cast, locations, props and costumes marked on the words (F11) */
  | { kind: "tags"; episodeId: string }
  | { kind: "elements"; subtype?: ElementSubtype }
  | { kind: "canvases" }
  | { kind: "assets" }
  | { kind: "makers"; page?: MakersPage }
  | { kind: "folder"; id: string }
  /** crew and sign-off rules of the production */
  | { kind: "crew" }
  /** the production's settings: name, picture, who can open it, deleting it */
  | { kind: "settings" };

export type SettingsPage = "members" | "claude" | "dailies" | "connections";

export type Route =
  | { view: "home" }
  /** every production of the workspace */
  | { view: "productions" }
  /** a new production (`step` 1–3) */
  | { view: "new"; step?: number }
  /** the shot composer without a shot yet (a new, loose shot) */
  | { view: "create" }
  /** the elements of the whole workspace */
  | { view: "elements" }
  /** an element shared by every production of the workspace */
  | { view: "workspace-element"; elementId: string }
  | { view: "explore" }
  | { view: "book"; id: string }
  | { view: "learn" }
  | { view: "credits" }
  /** the Higgsfield import: arrange a staged plan, then follow its runs (F9b B5a) */
  | { view: "import"; planId: string }
  /** the queue of what waits on me, one item at a time (built for a phone) */
  | { view: "signoff" }
  | { view: "project"; projectId: string; loc: Loc }
  | { view: "shot"; projectId: string; shotId: string }
  | { view: "element"; projectId: string; elementId: string }
  | { view: "canvas"; projectId: string | null; id: string }
  /** Workspace settings: members, roles and project access; your own Claude connections. */
  | { view: "settings"; page: SettingsPage }
  /** The consent page Claude sends a person to (OAuth). `query` is the link's query, kept as it came. */
  | { view: "connect"; query: string }
  /** Accepting an invitation. The token never travels in the route: it is read from the address once (see invite.ts). */
  | { view: "invite" }
  /** A review link opened by a guest (no account): outside the shell, before sign-in. */
  | { view: "share"; token: string }
  /** The unsubscribe link of a dailies mail. Outside the shell: the token is the credential, no sign-in needed. */
  | { view: "dailies-unsubscribe"; token: string }
  /** An address from the old tabbed admin (#/episode, #/canvases): has no project, so the app picks one. */
  | { view: "legacy"; target: "episodes" | "canvases" };

const ID = /^[0-9a-f-]{36}$/i;
/** The token in a dailies mail: url-safe characters only (the API takes 16 to 200). */
const UNSUB_TOKEN = /^[A-Za-z0-9._~-]{16,200}$/;
const SLUG = /^[a-z0-9-]{1,64}$/;
/** A review link token: 32 bytes, base64url. Anything else opens the "link not found" page. */
const SHARE_TOKEN = /^[A-Za-z0-9_-]{43}$/;
const SUBTYPES: readonly string[] = ["character", "location", "prop", "style"];
const isId = (s: string | undefined): s is string => !!s && ID.test(s);

export function parseRoute(hash: string): Route {
  // before the split: the query of a consent link may hold anything
  const connect = /^#\/?connect(?:\?(.*))?$/s.exec(hash);
  if (connect) return { view: "connect", query: connect[1] ?? "" };
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] === "canvas" && isId(parts[1]))
    return { view: "canvas", projectId: null, id: parts[1].toLowerCase() };
  if (parts[0] === "invite") return { view: "invite" };
  if (parts[0] === "s") return { view: "share", token: SHARE_TOKEN.test(parts[1] ?? "") ? parts[1]! : "" };
  if (parts[0] === "dailies" && parts[1] === "unsubscribe" && parts[2] && UNSUB_TOKEN.test(parts[2]))
    return { view: "dailies-unsubscribe", token: parts[2] };
  if (parts[0] === "settings")
    return {
      view: "settings",
      page:
        parts[1] === "claude" || parts[1] === "dailies" || parts[1] === "connections" ? parts[1] : "members",
    };
  if (parts[0] === "episode" || parts[0] === "episodes") return { view: "legacy", target: "episodes" };
  if (parts[0] === "elements")
    return isId(parts[1])
      ? { view: "workspace-element", elementId: parts[1].toLowerCase() }
      : { view: "elements" };
  if (parts[0] === "productions") return { view: "productions" };
  if (parts[0] === "new") {
    const step = Number(parts[1]);
    return step >= 2 && step <= 3 ? { view: "new", step } : { view: "new" };
  }
  if (parts[0] === "create") return { view: "create" };
  if (parts[0] === "explore")
    return parts[1] === "book" && parts[2] && SLUG.test(parts[2])
      ? { view: "book", id: parts[2] }
      : { view: "explore" };
  if (parts[0] === "learn") return { view: "learn" };
  if (parts[0] === "credits") return { view: "credits" };
  if (parts[0] === "signoff") return { view: "signoff" };
  if (parts[0] === "import" && isId(parts[1])) return { view: "import", planId: parts[1].toLowerCase() };
  if (parts[0] === "canvases") return { view: "legacy", target: "canvases" };
  if (parts[0] !== "p" || !isId(parts[1])) return { view: "home" };
  const projectId = parts[1].toLowerCase();
  const project = (loc: Loc): Route => ({ view: "project", projectId, loc });
  const [a, b, c, d, e, f] = parts.slice(2);
  switch (a) {
    case undefined:
      return project({ kind: "root" });
    case "episodes":
      return project({ kind: "episodes" });
    case "e":
      if (!isId(b)) return project({ kind: "episodes" });
      if (c === "s" && isId(d))
        return project({ kind: "scene", episodeId: b.toLowerCase(), id: d.toLowerCase() });
      if (c === "cut") return project({ kind: "cut", episodeId: b.toLowerCase() });
      if (c === "board") return project({ kind: "storyboard", episodeId: b.toLowerCase() });
      if (c === "tools")
        return project({
          kind: "tools",
          episodeId: b.toLowerCase(),
          ...(isId(d) ? { shotId: d.toLowerCase() } : {}),
        });
      if (c === "review") return project({ kind: "review", episodeId: b.toLowerCase() });
      if (c === "edit")
        return project({
          kind: "review",
          episodeId: b.toLowerCase(),
          ...(isId(d) ? { editId: d.toLowerCase(), ...(e === "open" ? { open: true } : {}) } : {}),
        });
      if (c === "blocking") return project({ kind: "blocking", episodeId: b.toLowerCase() });
      if (c === "tags") return project({ kind: "tags", episodeId: b.toLowerCase() });
      if (c === "d" && d && isDepartment(d))
        return project({
          kind: "episode",
          id: b.toLowerCase(),
          dept: d,
          ...(d === "writing" && e === "f" && (f === "script" || isId(f)) ? { file: f.toLowerCase() } : {}),
        });
      return project({ kind: "episode", id: b.toLowerCase() });
    case "elements":
      return project(
        b && SUBTYPES.includes(b) ? { kind: "elements", subtype: b as ElementSubtype } : { kind: "elements" },
      );
    case "canvases":
      return project({ kind: "canvases" });
    case "crew":
      return project({ kind: "crew" });
    case "settings":
      return project({ kind: "settings" });
    case "assets":
      return project({ kind: "assets" });
    case "makers":
      if (b === "brief" && (c === "new" || isId(c)))
        return project({ kind: "makers", page: { t: "brief", id: c.toLowerCase() } });
      if (b === "a" && isId(c))
        return project({ kind: "makers", page: { t: "assignment", id: c.toLowerCase() } });
      return project({ kind: "makers" });
    case "f":
      return isId(b) ? project({ kind: "folder", id: b.toLowerCase() }) : project({ kind: "root" });
    case "shot":
      return isId(b) ? { view: "shot", projectId, shotId: b.toLowerCase() } : project({ kind: "root" });
    case "element":
      return isId(b)
        ? { view: "element", projectId, elementId: b.toLowerCase() }
        : project({ kind: "elements" });
    case "canvas":
      return isId(b) ? { view: "canvas", projectId, id: b.toLowerCase() } : project({ kind: "canvases" });
    default:
      return project({ kind: "root" });
  }
}

export function formatLoc(projectId: string, loc: Loc): string {
  const p = `#/p/${projectId}`;
  switch (loc.kind) {
    case "root":
      return p;
    case "episodes":
      return `${p}/episodes`;
    case "episode":
      if (!loc.dept) return `${p}/e/${loc.id}`;
      return `${p}/e/${loc.id}/d/${loc.dept}${loc.file ? `/f/${loc.file}` : ""}`;
    case "crew":
      return `${p}/crew`;
    case "settings":
      return `${p}/settings`;
    case "scene":
      return `${p}/e/${loc.episodeId}/s/${loc.id}`;
    case "cut":
      return `${p}/e/${loc.episodeId}/cut`;
    case "storyboard":
      return `${p}/e/${loc.episodeId}/board`;
    case "tools":
      return `${p}/e/${loc.episodeId}/tools${loc.shotId ? `/${loc.shotId}` : ""}`;
    case "review":
      return `${p}/e/${loc.episodeId}/edit${loc.editId ? `/${loc.editId}${loc.open ? "/open" : ""}` : ""}`;
    case "blocking":
      return `${p}/e/${loc.episodeId}/blocking`;
    case "tags":
      return `${p}/e/${loc.episodeId}/tags`;
    case "elements":
      return loc.subtype ? `${p}/elements/${loc.subtype}` : `${p}/elements`;
    case "canvases":
      return `${p}/canvases`;
    case "assets":
      return `${p}/assets`;
    case "makers":
      if (!loc.page) return `${p}/makers`;
      return loc.page.t === "brief" ? `${p}/makers/brief/${loc.page.id}` : `${p}/makers/a/${loc.page.id}`;
    case "folder":
      return `${p}/f/${loc.id}`;
  }
}

export function formatRoute(r: Route): string {
  switch (r.view) {
    case "home":
      return "#/";
    case "productions":
    case "create":
    case "elements":
    case "explore":
    case "learn":
    case "credits":
    case "signoff":
      return `#/${r.view}`;
    case "new":
      return r.step && r.step > 1 ? `#/new/${r.step}` : "#/new";
    case "book":
      return `#/explore/book/${r.id}`;
    case "import":
      return `#/import/${r.planId}`;
    case "project":
      return formatLoc(r.projectId, r.loc);
    case "shot":
      return `#/p/${r.projectId}/shot/${r.shotId}`;
    case "element":
      return `#/p/${r.projectId}/element/${r.elementId}`;
    case "workspace-element":
      return `#/elements/${r.elementId}`;
    case "canvas":
      return r.projectId ? `#/p/${r.projectId}/canvas/${r.id}` : `#/canvas/${r.id}`;
    case "legacy":
      return `#/${r.target}`;
    case "settings":
      return `#/settings/${r.page}`;
    case "invite":
      return "#/invite";
    case "share":
      return `#/s/${r.token}`;
    case "dailies-unsubscribe":
      return `#/dailies/unsubscribe/${r.token}`;
    case "connect":
      return r.query ? `#/connect?${r.query}` : "#/connect";
  }
}

/** Where an old address goes once a project is known. */
export function legacyTarget(target: "episodes" | "elements" | "canvases", projectId: string): Route {
  return { view: "project", projectId, loc: { kind: target } };
}

/** Change the address without adding a history entry (redirects must not trap the Back button). */
export function replaceRoute(r: Route): void {
  window.location.replace(formatRoute(r));
}

/** Stable key of a folder location, for selection memory and tree focus. */
export function locKey(loc: Loc): string {
  switch (loc.kind) {
    case "episode":
    case "folder":
      return `${loc.kind}:${loc.id}`;
    case "scene":
      return `scene:${loc.id}`;
    case "cut":
      return `cut:${loc.episodeId}`;
    case "storyboard":
      return `storyboard:${loc.episodeId}`;
    case "tools":
      return `tools:${loc.episodeId}`;
    case "review":
      return `review:${loc.episodeId}${loc.editId ? `:${loc.editId}` : ""}${loc.open ? ":open" : ""}`;
    case "blocking":
      return `blocking:${loc.episodeId}`;
    case "tags":
      return `tags:${loc.episodeId}`;
    case "elements":
      return loc.subtype ? `elements:${loc.subtype}` : "elements";
    case "makers":
      // every page inside Makers is the same place in the tree
      return "makers";
    default:
      return loc.kind;
  }
}

export const sameLoc = (a: Loc, b: Loc): boolean => locKey(a) === locKey(b);

/**
 * The import modal's link as the API hands it to Claude (`arrangeUrl`: a path, /import/<planId>) turned into the
 * studio's hash address. Null for any other path.
 */
export function hashOfPath(pathname: string): string | null {
  const m = /^\/import\/([0-9a-f-]{36})\/?$/i.exec(pathname);
  return m && ID.test(m[1]!) ? `#/import/${m[1]!.toLowerCase()}` : null;
}
